import routes from '../../../application/routes';
import { ModuleUrls } from '../../../common/constants/userConstants';

interface RouteNode {
  path?: string;
  index?: boolean;
  children?: RouteNode[];
}

/**
 * Recursively flatten the route tree into a list of absolute paths.
 * Skips dynamic segments like `:fileId` and special routes (sso-login, etc.).
 */
function flattenRoutes(routeNodes: RouteNode[], parentPath = ''): string[] {
  const paths: string[] = [];

  for (const route of routeNodes) {
    let currentPath = parentPath;

    if (route.path != null) {
      const segment = route.path.replace(/^\//, ''); // strip leading /
      if (segment) {
        currentPath = parentPath ? `${parentPath}/${segment}`.replace(/\/+/g, '/') : `/${segment}`;
      }
    }

    // Add this path if it has one (skip index-only routes without a path)
    if (currentPath && currentPath !== parentPath) {
      paths.push(currentPath);
    }

    // Recurse into children
    if (route.children) {
      paths.push(...flattenRoutes(route.children, currentPath));
    }
  }

  return [...new Set(paths)];
}

/**
 * Get all registered application routes as absolute paths (e.g. "/eno1/upload").
 */
export function getAllRoutePaths(): string[] {
  return flattenRoutes(routes as RouteNode[]).sort();
}

/**
 * For a given module URL prefix (e.g. "/eno1"), return the list of
 * known route **suffixes** that can follow the prefix.
 *
 * Dynamic segments (`:param`) are kept in the output so the user
 * can see the pattern, but they are clearly marked.
 *
 * @example
 * getModuleRouteSuffixes('/eno1')
 * // → ['/', '/upload', '/messages/samples/:fileId', '/status', '/reports/no2', ...]
 */
export function getModuleRouteSuffixes(modulePrefix: string): string[] {
  if (!modulePrefix) return [];

  const allPaths = getAllRoutePaths();
  // The prefix without leading slash for matching (routes are stored as "/eno1/...")
  const prefixNorm = modulePrefix.startsWith('/') ? modulePrefix : `/${modulePrefix}`;

  const suffixes: string[] = [];

  for (const fullPath of allPaths) {
    if (fullPath === prefixNorm) {
      // The module root itself
      suffixes.push('');
    } else if (fullPath.startsWith(`${prefixNorm}/`)) {
      const suffix = fullPath.slice(prefixNorm.length); // includes leading "/"
      suffixes.push(suffix);
    }
  }

  return [...new Set(suffixes)].sort();
}

/**
 * Build a human-readable label for a route suffix, marking dynamic segments.
 *
 * @example
 * formatRouteSuffix('/messages/samples/:fileId')
 * // → '/messages/samples/:fileId'
 */
export function formatRouteSuffix(suffix: string): { label: string; hasDynamic: boolean } {
  const hasDynamic = suffix.includes(':');
  return { label: suffix, hasDynamic };
}

/**
 * Return a mapping of every module prefix → its route suffixes.
 * Useful for debugging / previewing.
 */
export function getAllModuleRouteMappings(): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  for (const [, prefix] of Object.entries(ModuleUrls)) {
    result[prefix] = getModuleRouteSuffixes(prefix);
  }
  return result;
}

/**
 * Resolve an actual browser URL to its matching route pattern.
 *
 * Compares each segment of the actual URL against all known route patterns.
 * Dynamic segments (`:param`) in a route pattern match any actual segment.
 *
 * @example
 * resolveUrlToPattern('/eno1/messages/samples/abc-123')
 * // → '/eno1/messages/samples/:fileId'
 *
 * resolveUrlToPattern('/eno1/upload')
 * // → '/eno1/upload'   (exact match, no dynamic parts)
 *
 * @returns The matching route pattern, or the original URL if no pattern matches.
 */
export function resolveUrlToPattern(actualUrl: string): string {
  const allPatterns = getAllRoutePaths();
  const actualParts = trimSlashes(actualUrl).split('/');

  for (const pattern of allPatterns) {
    const patternParts = trimSlashes(pattern).split('/');

    // Must have the same number of segments
    if (patternParts.length !== actualParts.length) continue;

    const matches = patternParts.every((part, i) => {
      if (part.startsWith(':')) return true; // dynamic segment matches anything
      return part.toLowerCase() === actualParts[i].toLowerCase();
    });

    if (matches) {
      // Return the pattern with the original leading slash
      return pattern.startsWith('/') ? pattern : `/${pattern}`;
    }
  }

  // No matching pattern found — return the original URL as-is
  return actualUrl;
}

function trimSlashes(url: string): string {
  return url.replace(/^\/+|\/+$/g, '');
}





